import { PermissionSyncStatus } from 'src/generated/graphql';

/**
 * The permission-sync loader rules, as a pure function.
 *
 * This lives apart from {@link ./AppBootstrap} because it is the part worth reading
 * and testing in isolation: the component only renders what this returns.
 *
 * Background: a user's permissions live in a DynamoDB cache filled by a background
 * sync. A user whose cache is empty or expired has nothing worth rendering — every
 * page below the bootstrap filters on those permissions, so rendering the app would
 * show a UI that looks broken rather than one that is still loading. So the
 * bootstrap waits. The rules decide whether waiting is needed, and when to stop.
 */

/**
 * How long a sync run may take before the user is told it is running long.
 *
 * Measured against the SERVER's `syncStartedAt` where there is one — see
 * {@link decideBootstrapAction}. Exceeding it is not an error: a run that is still
 * in progress keeps being waited on, because the caller cannot tell a slow run from
 * a dead one and the backend can. It only changes what the user is told.
 */
export const PERMISSION_SYNC_WAIT_BUDGET_MS = 3 * 60 * 1000;

/** Poll cadence while the loader is up. */
export const PERMISSION_SYNC_POLL_INTERVAL_MS = 2000;

/**
 * Grace window for a FAILED status.
 *
 * A re-trigger fires during `getUserProfile`, but the workflow writes IN_PROGRESS
 * asynchronously, so the first status read can legitimately return the PREVIOUS
 * run's FAILED. Long enough for two polls to land, short enough that a genuinely
 * failed sync surfaces promptly.
 */
export const PERMISSION_SYNC_FAILED_GRACE_MS = 5000;

/** The status payload the rules read. Mirrors `GetPermissionSyncStatus`. */
export interface PermissionSyncSnapshot {
  hasUnexpiredCachedPermissions: boolean;
  syncStatus?: PermissionSyncStatus | null;
  syncStartedAt?: number | null;
}

/**
 * Client-side observation times, for the two states with no server timestamp to
 * measure against. Both are "when this bootstrap first saw that state", and both are
 * legitimately reset by a page refresh — see {@link decideBootstrapAction}.
 */
export interface ClientWaitClocks {
  /** When a wait with no server start time was first observed, if ever. */
  waitWithoutServerStartSince: number | null;
  /** When a FAILED status was first observed, if ever. */
  failedFirstSeenAt: number | null;
}

/**
 * What the bootstrap should do, and why.
 *
 * `WAIT` carries `reevaluateAt`: the epoch millisecond at which this same reading
 * stops meaning "wait". The caller schedules one timer for it, so a wait ends at its
 * deadline even if no poll happens to land at that moment — the decision must not
 * silently inherit the poll cadence as its resolution.
 *
 * `WAIT_EXTENDED` has no deadline. It means the run has outlived the budget but is
 * still in progress, so there is nothing left to time out against: only a new
 * snapshot can move it. Kept distinct from `WAIT` because it is the one state whose
 * copy tells the user it is running long, and distinct from `GIVE_UP` because
 * refreshing cannot help — the trigger is discarded while the run holds ORCA's
 * singleton key, so a refresh returns this same reading.
 */
export type BootstrapAction =
  | { action: 'RENDER'; reason: 'USABLE_CACHE' }
  | {
      action: 'WAIT';
      reason: 'RUN_IN_PROGRESS' | 'AWAITING_STATUS' | 'SYNC_FAILED';
      reevaluateAt: number;
    }
  | { action: 'WAIT_EXTENDED'; reason: 'RUN_OVER_BUDGET' }
  | { action: 'GIVE_UP'; reason: 'AWAITING_STATUS' | 'SYNC_FAILED' };

/**
 * Applies the loader rules to one status reading.
 *
 * WHY AN IN-PROGRESS RUN IS MEASURED AGAINST THE SERVER'S `syncStartedAt`, NOT A
 * CLIENT TIMER: a client timer starts when the page loads, so it restarts on every
 * refresh and could never tell a long-running sync from a fresh one. `syncStartedAt`
 * is the time the RUN began, so `now - syncStartedAt` measures the run's total age
 * however many times the page has been reloaded.
 *
 * The two client-anchored states do not have that hazard: in both, a refresh re-runs
 * `getUserProfile`, which re-fires the trigger, and neither has a run holding ORCA's
 * singleton key to discard it. A refresh there starts a genuinely new attempt, so
 * restarting its clock is correct rather than a loophole.
 *
 * @param snapshot latest status reading
 * @param now epoch millis (injected so the rules stay pure and testable)
 * @param clocks first-observation times for the client-anchored states
 */
export function decideBootstrapAction(
  snapshot: PermissionSyncSnapshot,
  now: number,
  clocks: ClientWaitClocks,
): BootstrapAction {
  // Usable cache, whatever the status. A running refresh is background work and a
  // failed background refresh is invisible: the next bootstrap retries it. Checked
  // first precisely so IN_PROGRESS and FAILED never reach the cases below when there
  // is something to show.
  if (snapshot.hasUnexpiredCachedPermissions) {
    return { action: 'RENDER', reason: 'USABLE_CACHE' };
  }

  const { syncStatus, syncStartedAt } = snapshot;

  // The only state with server truth to measure: a run in progress, which always
  // carries its start time because one DynamoDB write sets both.
  if (
    syncStatus === PermissionSyncStatus.InProgress &&
    typeof syncStartedAt === 'number'
  ) {
    return now - syncStartedAt > PERMISSION_SYNC_WAIT_BUDGET_MS
      ? // Over budget, but the run is alive as far as anyone here can tell. Keep
        // waiting and say so. Erroring would be worse than useless: ORCA discards a
        // re-trigger while this run holds the singleton key, so the refresh the
        // error page advises would return this very reading, forever.
        { action: 'WAIT_EXTENDED', reason: 'RUN_OVER_BUDGET' }
      : {
          action: 'WAIT',
          reason: 'RUN_IN_PROGRESS',
          reevaluateAt: syncStartedAt + PERMISSION_SYNC_WAIT_BUDGET_MS,
        };
  }

  // FAILED. Keep polling briefly: a re-trigger fired during `getUserProfile` and its
  // IN_PROGRESS write may still be landing, in which case the next poll flips us to
  // the in-progress case. If FAILED persists past the grace window it is the real
  // answer, and giving up is honest — the errored execution makes the next trigger
  // eligible for ORCA's rerun recovery, so refreshing does retry.
  if (syncStatus === PermissionSyncStatus.Failed) {
    const failedSince = clocks.failedFirstSeenAt ?? now;
    return now - failedSince > PERMISSION_SYNC_FAILED_GRACE_MS
      ? { action: 'GIVE_UP', reason: 'SYNC_FAILED' }
      : {
          action: 'WAIT',
          reason: 'SYNC_FAILED',
          reevaluateAt: failedSince + PERMISSION_SYNC_FAILED_GRACE_MS,
        };
  }

  // Status absent, or a COMPLETED run whose cache is already stale. Both are the same
  // situation: the trigger fired during `getUserProfile`, the workflow's status write
  // is asynchronous, and it has not landed yet. Wait for it.
  //
  // Bounded from first observation — the only clock available, since there is no run
  // to ask. Giving up here is honest for the same reason as FAILED: no run holds the
  // singleton key, so a refresh re-fires the trigger for real.
  const waitingSince = clocks.waitWithoutServerStartSince ?? now;
  return now - waitingSince > PERMISSION_SYNC_WAIT_BUDGET_MS
    ? { action: 'GIVE_UP', reason: 'AWAITING_STATUS' }
    : {
        action: 'WAIT',
        reason: 'AWAITING_STATUS',
        reevaluateAt: waitingSince + PERMISSION_SYNC_WAIT_BUDGET_MS,
      };
}
